import { CircleCircle, WorkflowWorkflowType } from '@calm/evexia-web-sdk';
import createWorkflowApi from '@rhg/libs/api/create-workflow-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryKeys,
    useMutation,
} from '@rhg/libs/react-query';
import {
    workflowByDefnIdForRecipientQueryOptions,
    workflowByTypeForRecipientQueryOptions,
} from '../use-workflows';

interface WorkflowNextStepParameters {
    circle: CircleCircle;
    workflowType?: WorkflowWorkflowType;
    workflowDefnId?: string | undefined;
}

export const workflowNextStepQueryOptions = {
    queryKey: ({
        workflowDefnId,
        recipientId,
    }: {
        workflowDefnId: string | undefined;
        recipientId: number;
    }) => {
        return [
            queryKeys.workflowNextStepByDefnId,
            recipientId,
            workflowDefnId,
        ];
    },
    queryFn: async ({
        getAccessTokenSilently,
        circleAuthorizationToken,
        recipientId,
        workflowDefnId,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        circleAuthorizationToken: string;
        recipientId: number;
        workflowDefnId: string;
    }) => {
        const workflowApi = createWorkflowApi(getAccessTokenSilently);

        const response = await workflowApi.v1GetNextStep({
            workflowDefnId,
            circleAuthorizationToken,
            recipientId,
        });

        return response.data;
    },
    queryOptions: ({
        workflowDefnId,
        recipientId,
        circleAuthorizationToken,
        isAuthenticated,
    }: {
        workflowDefnId: string;
        recipientId: number;
        circleAuthorizationToken: string;
        isAuthenticated: boolean;
    }) => {
        return {
            enabled: !!(
                isAuthenticated &&
                workflowDefnId &&
                recipientId &&
                circleAuthorizationToken
            ),
        };
    },
};

export function useFetchWorkflowNextStep() {
    const { getAccessTokenSilently } = useAuth();
    const { queryFn } = workflowNextStepQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            workflowDefnId,
            circle,
        }: WorkflowNextStepParameters) => {
            return queryFn({
                getAccessTokenSilently,
                workflowDefnId: workflowDefnId!,
                recipientId: circle.recipient.id,
                circleAuthorizationToken: circle?.authorizationJWT ?? '',
            });
        },
        onSuccess: async (
            response,
            { circle, workflowDefnId, workflowType }
        ) => {
            if (response) {
                // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
                await Promise.all([
                    invalidateQueryKey(
                        workflowByTypeForRecipientQueryOptions.queryKey({
                            workflowType,
                            recipientId: circle.recipient.id,
                        })
                    ),
                    invalidateQueryKey(
                        workflowByDefnIdForRecipientQueryOptions.queryKey({
                            workflowDefnId,
                            recipientId: circle.recipient.id,
                        })
                    ),
                ]);
            }
        },
    });
    return {
        fetchWorkflowNextStep: mutate,
        fetchWorkflowNextStepAsync: mutateAsync,
        ...otherProps,
    };
}
